import React from 'react';
import { Card, CardContent, Typography, Stack, Chip, Box, Avatar } from '@mui/material';
import { AlertCircle } from 'lucide-react';
import { Task } from '../../types';

interface KanbanCardProps {
  task: Task;
  isDragging?: boolean;
}

const KanbanCard: React.FC<KanbanCardProps> = ({ task, isDragging = false }) => {
  const getPriorityColor = (priority: string): string => {
    switch (priority.toLowerCase()) {
      case 'high':
        return '#ff5252';
      case 'medium':
        return '#ff9800';
      case 'low':
        return '#4caf50';
      default:
        return '#9e9e9e';
    }
  };

  const getPriorityChipColor = (priority: string): any => {
    switch (priority.toLowerCase()) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <Card
      sx={{
        cursor: 'grab',
        userSelect: 'none',
        transition: 'all 0.2s',
        boxShadow: isDragging ? '0 5px 15px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
        '&:hover': {
          boxShadow: '0 3px 8px rgba(0,0,0,0.15)',
          transform: isDragging ? 'none' : 'translateY(-2px)',
        },
        borderLeft: `4px solid ${getPriorityColor(task.priority)}`,
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            mb: 1,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {task.title}
        </Typography>

        <Stack gap={1}>
          {/* Priority and Status Badges */}
          <Stack direction="row" gap={0.5} flexWrap="wrap">
            <Chip
              label={task.priority}
              size="small"
              color={getPriorityChipColor(task.priority)}
              variant="outlined"
            />
          </Stack>

          {/* Due Date */}
          {task.dueDate && (
            <Typography variant="caption" sx={{ color: '#666' }}>
              Due: {new Date(task.dueDate).toLocaleDateString()}
            </Typography>
          )}

          {/* Blocked Indicator */}
          {task.blockedCount && task.blockedCount > 0 && (
            <Stack direction="row" alignItems="center" gap={0.5} sx={{ color: '#ff5252' }}>
              <AlertCircle size={14} />
              <Typography variant="caption">
                Blocked by {task.blockedCount} task{task.blockedCount !== 1 ? 's' : ''}
              </Typography>
            </Stack>
          )}

          {/* Dependency Count */}
          {task.blocksCount && task.blocksCount > 0 && (
            <Typography variant="caption" sx={{ color: '#1976d2' }}>
              Blocks {task.blocksCount} task{task.blocksCount !== 1 ? 's' : ''}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default KanbanCard;
